import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { LayoutData } from '@/types'

import { Seo } from '@/components/generals/seo'
import Container from '@/components/generals/container'
import PageHero from '@/components/generals/page-hero'
import CtaBanner from '@/components/generals/cta-banner'
import AppImage from '@/components/generals/app-image'
import SectionHeading from '@/components/generals/section-heading'
import { getLayoutData } from '@/lib/catalog'
import { aboutPillars, clientTypes } from '@/constants/content'
import { revalidateSeconds, sectionImages } from '@/constants/site'

interface NosotrosProps {
    layout: LayoutData
}

const Nosotros: FC<NosotrosProps> = (): ReactNode => {
    return (
        <>
            <Seo
                title='Nosotros'
                description='TROFÉA concibe cada reconocimiento como una pieza destinada a conservar el valor de un logro, con personalización, estética y materiales seleccionados.'
            />

            <PageHero
                eyebrow='Nosotros'
                title='Creamos reconocimientos que perduran.'
                description='En TROFÉA concebimos cada reconocimiento como una pieza destinada a conservar el valor de un logro. Nuestra propuesta se centra en la personalización, la estética y la selección de materiales y acabados que permitan representar cada ocasión de manera especial.'
                breadcrumbs={[
                    { label: 'Inicio', href: '/' },
                    { label: 'Nosotros' }
                ]}
            />

            <section aria-label='Presentación' className='bg-black'>
                <Container className='pt-16 md:pt-20'>
                    <div className='relative aspect-[16/10] overflow-hidden rounded-sm border border-ivory/10 sm:aspect-[21/9]'>
                        <AppImage
                            src={sectionImages.nosotros}
                            alt='Trofeo de latón cepillado dentro de un estuche negro forrado en satén marfil'
                            sizes='(min-width: 1280px) 1200px, 100vw'
                            className='object-[70%_center]'
                        />
                    </div>
                </Container>
            </section>

            <section id='filosofia' aria-labelledby='filosofia-titulo' className='bg-black py-20 md:py-28'>
                <Container className='grid gap-14 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            id='filosofia-titulo'
                            eyebrow='Nuestra propuesta'
                            title='El valor de un logro merece un reconocimiento extraordinario.'
                        />
                    </div>

                    <div className='flex flex-col gap-6 text-lg leading-relaxed text-ivory/80 lg:col-span-7'>
                        <p>
                            Un reconocimiento no es solo un objeto: es la forma tangible de decir que un esfuerzo fue visto y valorado. Por eso cada pieza debe estar a la altura del momento en que se entrega y del lugar donde se conservará después.
                        </p>
                        <p>
                            Trabajamos con empresas, instituciones, centros educativos y organizaciones que buscan algo más que un trofeo genérico. Escuchamos qué se celebra, para quién y en qué contexto, y a partir de ahí proponemos formas, materiales e inscripciones con intención.
                        </p>
                    </div>
                </Container>
            </section>

            <section id='pilares' aria-labelledby='pilares-titulo' className='surface-grain relative bg-graphite py-20 md:py-28'>
                <Container>
                    <h2 id='pilares-titulo' className='sr-only'>Nuestros pilares</h2>

                    <ul className='grid gap-5 md:grid-cols-3'>
                        {aboutPillars.map((pillar, index) => {
                            const Icon = pillar.icon

                            return (
                                <li key={pillar.title} className='reveal flex flex-col gap-6 rounded-sm border border-ivory/10 bg-black p-8 md:p-10'>
                                    <div className='flex items-center justify-between'>
                                        <span className='inline-flex size-12 items-center justify-center rounded-sm border border-brass/40 text-brass'>
                                            <Icon aria-hidden='true' className='size-6' />
                                        </span>
                                        <span aria-hidden='true' className='font-heading text-5xl leading-none text-ivory/10'>0{index + 1}</span>
                                    </div>

                                    <h3 className='font-heading text-3xl leading-tight font-medium text-foreground'>{pillar.title}</h3>
                                    <p className='text-sm leading-relaxed text-muted-foreground'>{pillar.description}</p>
                                </li>
                            )
                        })}
                    </ul>
                </Container>
            </section>

            <section id='clientes' aria-labelledby='clientes-titulo' className='bg-black py-20 md:py-28'>
                <Container>
                    <SectionHeading
                        id='clientes-titulo'
                        eyebrow='A quiénes acompañamos'
                        title='Una propuesta para quienes celebran logros.'
                        description='Nuestra propuesta está pensada para organizaciones y personas que necesitan reconocer, premiar o conmemorar con piezas cuidadas.'
                    />

                    <ul className='mt-14 grid gap-px overflow-hidden rounded-sm border border-ivory/10 bg-ivory/10 sm:grid-cols-2 lg:grid-cols-4'>
                        {clientTypes.map(client => {
                            const Icon = client.icon

                            return (
                                <li key={client.title} className='flex flex-col gap-4 bg-black p-7 transition-colors duration-500 hover:bg-graphite'>
                                    <Icon aria-hidden='true' className='size-6 text-brass' />
                                    <h3 className='font-heading text-2xl leading-tight font-medium text-foreground'>{client.title}</h3>
                                    <p className='text-sm leading-relaxed text-muted-foreground'>{client.description}</p>
                                </li>
                            )
                        })}
                    </ul>
                </Container>
            </section>

            <section id='compromiso' aria-labelledby='compromiso-titulo' className='surface-grain relative bg-graphite py-20 md:py-28'>
                <Container size='narrow' className='flex flex-col items-center gap-8 text-center'>
                    <span aria-hidden='true' className='font-heading text-7xl leading-none text-brass'>“</span>

                    <h2 id='compromiso-titulo' className='font-heading text-3xl leading-snug font-medium text-foreground italic sm:text-4xl'>
                        Cuidamos la legibilidad de cada grabado, la proporción de cada pieza y su presentación final, porque los detalles son lo que se recuerda.
                    </h2>

                    <p className='text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-brass'>Compromiso con la calidad</p>
                </Container>
            </section>

            <CtaBanner />
        </>
    )
}

export const getStaticProps: GetStaticProps<NosotrosProps> = async () => {
    const layout = await getLayoutData()

    return {
        props: {
            layout
        },
        revalidate: revalidateSeconds
    }
}

export default Nosotros
